/*
 * Formatador de valores monetários pt-BR STRING-BASED (FE-2 CP-A — ADR 0026).
 *
 * POR QUE STRING-BASED (e não `Number`/`Intl.NumberFormat` sobre `number`):
 * o wire entrega `NUMERIC(18,2)` como string decimal (ex.: `"99999999999999.99"`).
 * Converter para `number` ANTES de formatar perde precisão — IEEE-754 não
 * representa todo decimal exato e qualquer valor acima de `Number.MAX_SAFE_INTEGER`
 * (9.007.199.254.740.991) é arredondado. `Number("99999999999999.99")` já vira
 * `99999999999999.98...`. Para um sistema financeiro, isso é inaceitável.
 *
 * Por isso a formatação é puramente TEXTUAL: separa parte inteira/decimal por
 * string, agrupa milhares inserindo `.` a cada 3 dígitos da direita para a
 * esquerda, e usa `,` como separador decimal. NENHUM `Number`/`parseFloat`/
 * `parseInt` toca o valor monetário.
 */

/**
 * Formata uma string decimal do wire para o padrão monetário pt-BR.
 *
 * Entrada: string decimal sinalizada, ponto como separador decimal
 * (ex.: `"1234567.89"`, `"-150.00"`, `"0.00"`, `"99999999999999.99"`).
 * Saída: agrupamento de milhar com `.`, separador decimal `,`, sinal preservado
 * (ex.: `"1.234.567,89"`, `"-150,00"`, `"0,00"`, `"99.999.999.999.999,99"`).
 *
 * Robusto a: ausência de parte decimal (`"42"` → `"42"`), parte inteira `"0"`,
 * sinal negativo, e valores ARBITRARIAMENTE grandes (a razão de ser string-based).
 *
 * Não tenta validar/normalizar entradas malformadas além do mínimo: assume o
 * contrato do wire (decimal bem-formado). Entrada vazia → `""`.
 */
export function formatarDecimal(valor: string): string {
  if (valor === "") {
    return "";
  }

  // Extrai o sinal por caractere — nunca por aritmética (string-based).
  let sinal = "";
  let corpo = valor;
  if (corpo.startsWith("-")) {
    sinal = "-";
    corpo = corpo.slice(1);
  } else if (corpo.startsWith("+")) {
    // `+` explícito é descartado (não se imprime sinal positivo).
    corpo = corpo.slice(1);
  }

  // Split inteiro/decimal pelo PONTO do wire. Sem parte decimal → string vazia.
  const indicePonto = corpo.indexOf(".");
  const parteInteira = indicePonto === -1 ? corpo : corpo.slice(0, indicePonto);
  const parteDecimal = indicePonto === -1 ? "" : corpo.slice(indicePonto + 1);

  const inteiraAgrupada = agruparMilhares(parteInteira);

  // Recompõe: sinal + inteira agrupada [+ "," + decimal].
  if (parteDecimal === "") {
    return `${sinal}${inteiraAgrupada}`;
  }
  return `${sinal}${inteiraAgrupada},${parteDecimal}`;
}

/**
 * Normaliza uma string DIGITADA pelo usuário (campo de entrada de money) na string
 * canônica decimal `"[-]INTEIRO.DD"` (ponto decimal, 2 casas) — ou `null` se o
 * formato for inválido.
 *
 * POR QUE STRING-BASED (regra inviolável do projeto): money NUNCA vira `float`.
 * Esta função NÃO usa `Number`/`parseFloat`/`parseInt`/`Math` — toda a análise é
 * por caractere/`String`. Assim a entrada `"99999999999999,99"` (acima de
 * `Number.MAX_SAFE_INTEGER`) é preservada sem arredondamento, e o backend
 * (`NUMERIC(18,2)`) é a única autoridade de magnitude — esta função NÃO impõe cap.
 *
 * HEURÍSTICA pt-BR vs canônico:
 *  - Se houver vírgula, o ÚLTIMO separador (a vírgula) é o decimal; todos os pontos
 *    são separadores de milhar e são REMOVIDOS textualmente (ex.: `"1.234.567,89"`
 *    → inteiro `"1234567"`, decimal `"89"`).
 *  - Se NÃO houver vírgula, um único ponto é o decimal canônico (ex.: `"1500.00"`);
 *    pontos múltiplos sem vírgula são AMBÍGUOS (milhar sem decimal) → inválido.
 *
 * VALIDAÇÃO de formato: aceita dígitos + um sinal `-` opcional no início (limites de
 * overdraft podem ser negativos) + separadores válidos; a parte decimal tem ≤2
 * casas. Inválido (letras, sinal repetido/mal-posicionado, >2 casas, separadores
 * inconsistentes, vazio) → `null`. A saída SEMPRE tem 2 casas decimais (preenche
 * com `"00"`/`"0"` quando faltam).
 *
 * Exemplos: `"1.500,00"`→`"1500.00"`, `"1500"`→`"1500.00"`, `"-150,00"`→`"-150.00"`,
 * `"1234567.89"`→`"1234567.89"`; `"abc"`/`""`/`"1,5,5"`/`"1.5.5"`/`"10.123"`→`null`.
 */
export function normalizarDecimalEntrada(entrada: string): string | null {
  // Trim textual; entrada vazia é inválida (não há money "vazio" canônico).
  const limpo = entrada.trim();
  if (limpo === "") {
    return null;
  }

  // Extrai o sinal `-` opcional do início (string-based, sem aritmética).
  let sinal = "";
  let corpo = limpo;
  if (corpo.startsWith("-")) {
    sinal = "-";
    corpo = corpo.slice(1);
  } else if (corpo.startsWith("+")) {
    // `+` explícito é aceito mas descartado (não imprimimos sinal positivo).
    corpo = corpo.slice(1);
  }
  if (corpo === "") {
    // Era só um sinal, sem dígitos.
    return null;
  }

  // Só são permitidos dígitos, `.` e `,` no corpo (sinal já foi removido).
  for (const caractere of corpo) {
    const ehDigito = caractere >= "0" && caractere <= "9";
    if (!ehDigito && caractere !== "." && caractere !== ",") {
      return null;
    }
  }

  // Separa parte inteira e decimal conforme a heurística pt-BR vs canônico.
  let parteInteira: string;
  let parteDecimal: string;

  const indiceVirgula = corpo.indexOf(",");
  if (indiceVirgula !== -1) {
    // pt-BR: a vírgula é o decimal. Não pode haver mais de uma vírgula.
    if (corpo.indexOf(",", indiceVirgula + 1) !== -1) {
      return null;
    }
    const antesVirgula = corpo.slice(0, indiceVirgula);
    parteDecimal = corpo.slice(indiceVirgula + 1);
    // Pontos antes da vírgula são separadores de milhar → removidos textualmente.
    // Depois da vírgula não pode haver ponto.
    if (parteDecimal.includes(".")) {
      return null;
    }
    parteInteira = antesVirgula.split(".").join("");
  } else {
    // Canônico: no máximo UM ponto, que é o decimal. Mais de um ponto sem vírgula é
    // ambíguo (milhar sem decimal explícito) e rejeitado.
    const indicePonto = corpo.indexOf(".");
    if (indicePonto === -1) {
      parteInteira = corpo;
      parteDecimal = "";
    } else {
      if (corpo.indexOf(".", indicePonto + 1) !== -1) {
        return null;
      }
      parteInteira = corpo.slice(0, indicePonto);
      parteDecimal = corpo.slice(indicePonto + 1);
    }
  }

  // A parte inteira pode ser vazia (ex.: ",50" → "0"); tem de ser só dígitos.
  if (parteInteira === "") {
    parteInteira = "0";
  }
  if (!apenasDigitos(parteInteira)) {
    return null;
  }
  // A parte decimal é opcional, ≤2 casas, só dígitos.
  if (parteDecimal.length > 2) {
    return null;
  }
  if (parteDecimal !== "" && !apenasDigitos(parteDecimal)) {
    return null;
  }

  // Completa para sempre 2 casas decimais (string-based).
  const decimalCanonico = (parteDecimal + "00").slice(0, 2);

  return `${sinal}${parteInteira}.${decimalCanonico}`;
}

/** `true` sse `texto` é não-vazio e composto SÓ de dígitos `0`–`9` (sem `Number`). */
function apenasDigitos(texto: string): boolean {
  if (texto === "") {
    return false;
  }
  for (const caractere of texto) {
    if (caractere < "0" || caractere > "9") {
      return false;
    }
  }
  return true;
}

/**
 * Insere `.` a cada 3 dígitos da DIREITA para a ESQUERDA na parte inteira.
 *
 * Puramente textual: percorre os caracteres do fim para o começo, contando
 * dígitos e inserindo o separador a cada grupo de 3. `"1234567"` → `"1.234.567"`,
 * `"0"` → `"0"`, `""` → `"0"` (defesa: parte inteira ausente vira `"0"`).
 */
function agruparMilhares(digitos: string): string {
  if (digitos === "") {
    // Defesa contra entradas tipo `".50"` (sem parte inteira): assume `"0"`.
    return "0";
  }

  const partes: string[] = [];
  let contador = 0;
  let grupoAtual = "";

  // Da direita para a esquerda: cada 3 dígitos vira um grupo.
  for (let i = digitos.length - 1; i >= 0; i--) {
    grupoAtual = digitos[i] + grupoAtual;
    contador += 1;
    if (contador === 3) {
      partes.unshift(grupoAtual);
      grupoAtual = "";
      contador = 0;
    }
  }
  // Resto (1 ou 2 dígitos no início) entra como primeiro grupo.
  if (grupoAtual !== "") {
    partes.unshift(grupoAtual);
  }

  return partes.join(".");
}
